import React, { useEffect, useMemo, useState } from "react";
import { Drawer } from "vaul";

interface StockTickerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tickers: string[];
  initialTicker?: string | null;
  onSelect: (ticker: string) => void;
}

export const StockTickerDrawer: React.FC<StockTickerDrawerProps> = ({
  open,
  onOpenChange,
  tickers,
  initialTicker,
  onSelect,
}) => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setQuery(initialTicker ?? "");
    setSelected(initialTicker ?? null);
  }, [open, initialTicker]);

  const visible = useMemo(() => {
    const needle = query.trim().toUpperCase();
    const sorted = [...tickers].sort();
    if (!needle) return sorted.slice(0, 80);
    return sorted.filter((ticker) => ticker.includes(needle)).slice(0, 80);
  }, [query, tickers]);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[120]" />
        <Drawer.Content className="bg-[#1a1b20] flex flex-col rounded-t-xl fixed bottom-0 left-0 right-0 max-h-[75vh] z-[121]">
          <div className="px-4 border-b border-sky-500/30 pb-3 pt-2">
            <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
            <h2 className="text-md font-bold text-text-primary">
              Trade a tokenized stock
            </h2>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value.toUpperCase())}
              placeholder="Search AAPL, NVDA, TSLA"
              className="mt-3 w-full rounded-lg border border-gray-600 bg-primary/40 p-3 text-sm uppercase text-text-primary focus:border-accent focus:outline-none"
            />
          </div>
          <div className="flex-1 overflow-y-auto p-2">
            {visible.length === 0 && (
              <p className="px-2 py-6 text-center text-sm text-text-secondary">
                No listed stock matches that ticker
              </p>
            )}
            {visible.map((ticker) => (
              <button
                key={ticker}
                type="button"
                onClick={() => setSelected(ticker)}
                className={`mb-1 w-full rounded-lg px-3 py-2 text-left text-sm font-semibold ${
                  selected === ticker
                    ? "bg-sky-500/20 text-sky-100"
                    : "text-text-primary hover:bg-white/5"
                }`}
              >
                {ticker}
              </button>
            ))}
          </div>
          <div className="p-4">
            <button
              type="button"
              disabled={!selected}
              onClick={() => selected && onSelect(selected)}
              className="w-full rounded-lg bg-accent py-3 text-sm font-semibold text-white disabled:opacity-50"
            >
              {selected ? `Trade ${selected}` : "Select a stock"}
            </button>
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};
